import React, { useRef, useEffect, useState } from 'react';
import { ChromeIcon, CreditCardIcon, Download, Play, Video } from 'lucide-react';
import { trackButtonClick, trackConversion } from '../../lib/analytics';
import { trackButtonClick as trackFirebaseButtonClick } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';

interface HeroContentProps {
  onOpenDemo: () => void;
}

export function HeroContent({ onOpenDemo }: HeroContentProps) {
  const navigate = useNavigate();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [revealText, setRevealText] = useState(false);
  
  useEffect(() => {
    // Add delay before fading in text for a more elegant reveal
    const timer = setTimeout(() => {
      setRevealText(true);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, []);

  const handleStartPractice = () => {
    // Track button click with unified analytics (includes Firebase, Meta Pixel, GA4, Clarity)
    trackButtonClick('Start Practice', 'Hero Section', {
      button_type: 'primary_cta',
      button_position: 'hero_main',
      conversion_intent: 'sign_up',
      utm_source: 'landing_page',
      utm_medium: 'hero_section',
      utm_campaign: 'start_practice'
    });
    
    // Track conversion event with unified analytics
    trackConversion('sign_up', 20);

    // Keep Firebase analytics for backward compatibility (handled in unified system now)
    trackFirebaseButtonClick('Start Practice', 'Hero Section', {
      button_type: 'primary_cta',
      conversion_intent: 'sign_up'
    });
    
    const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
    // Open the app page in a new tab
    window.open(`${platformUrl}/login?utm_source=landing_page&utm_medium=hero_section&utm_campaign=start_practice`, '_blank');
  };

  const handleGetChromeExtension = () => {
    // Track button click with unified analytics
    trackButtonClick('Get Chrome Extension', 'Hero Section', {
      button_type: 'secondary_cta',
      button_position: 'hero_secondary',
      conversion_intent: 'get_chrome_extension',
      utm_source: 'landing_page',
      utm_medium: 'hero_section',
      utm_campaign: 'get_chrome_extension'
    });
    
    // Track conversion event with unified analytics
    trackConversion('get_chrome_extension', 0);

    // Keep Firebase analytics for backward compatibility
    trackFirebaseButtonClick('Get Chrome Extension', 'Hero Section', {
      button_type: 'secondary_cta',
      conversion_intent: 'get_chrome_extension'
    });
    
    // Open our chrome extension page in a new tab
    window.open(`https://chromewebstore.google.com/detail/mocktrail-resume-score-in/ljopoadaipkmgclmpljgachiciakomgo`, '_blank');
  };

  return (
    <div className="text-center">
      {/* Elegant reveal animation for heading */}
      <h1 
        ref={headingRef}
        className={`
          text-6xl sm:text-6xl md:text-[5.6rem] font-bold mb-6 
          bg-clip-text text-transparent bg-gradient-to-r 
          from-primary-light via-secondary-light to-primary-light 
          dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark 
          animate-gradient bg-[length:200%_auto] 
          transition-all duration-1500 ease-out
          ${revealText ? 'opacity-100 transform-none' : 'opacity-0 translate-y-8 blur-sm'}
        `}
      >
       Your AI Interview Coach for Success
      </h1>
      
      <p className={`
        text-lg md:text-lg text-text-secondary-light dark:text-text-secondary-dark mb-10 max-w-3xl mx-auto 
        transition-all duration-1500 ease-out delay-150
        ${revealText ? 'opacity-100 transform-none' : 'opacity-0 translate-y-8 blur-sm'}
      `}>
        Master your interviews with <span className='text-text-primary-light dark:text-text-primary-dark'>MockTrail</span>, the #1 <strong className="text-text-primary-light dark:text-text-primary-dark">AI Interview practice platform</strong>. Get personalized mock interviews, instant AI feedback, and skill-based career insights. 
      </p>
      
      <div className={`
        flex flex-col sm:flex-row gap-4 justify-center items-center
        transition-all duration-1500 ease-out delay-300
        ${revealText ? 'opacity-100 transform-none' : 'opacity-0 translate-y-8 blur-sm'}
      `}>
        <button 
          className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto relative overflow-hidden group py-4 px-9"
          onClick={handleStartPractice}
        >
          {/* Button shine effect */}
          <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 -translate-x-full group-hover:translate-x-full transition-all duration-1000 ease-out"></span>
          
          <Video className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
          <span className="relative z-10">Start Practice</span>
        </button>
        
        <button 
          onClick={handleGetChromeExtension}
          className="btn-secondary inline-flex items-center justify-center gap-2 w-full sm:w-auto relative overflow-hidden group py-3.5"
        >
          {/* Button shine effect */}
          <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 -translate-x-full group-hover:translate-x-full transition-all duration-1000 ease-out"></span>
          
          <svg 
            className="w-5 h-5 transition-transform duration-300 group-hover:scale-110 -mt-0.5" 
            xmlns="http://www.w3.org/2000/svg" 
            xmlnsXlink="http://www.w3.org/1999/xlink" 
            viewBox="0 0 48 48" 
            height="48" 
            width="48"
          >
            <defs>
              <linearGradient id="a" x1="3.2173" y1="15" x2="44.7812" y2="15" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#d93025" />
                <stop offset="1" stopColor="#ea4335" />
              </linearGradient>
              <linearGradient id="b" x1="20.7219" y1="47.6791" x2="41.5039" y2="11.6837" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#fcc934" />
                <stop offset="1" stopColor="#fbbc04" />
              </linearGradient>
              <linearGradient id="c" x1="26.5981" y1="46.5015" x2="5.8161" y2="10.506" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#1e8e3e" />
                <stop offset="1" stopColor="#34a853" />
              </linearGradient>
            </defs>
            <circle cx="24" cy="23.9947" r="12" style={{fill:"#fff"}} />
            <path d="M3.2154,36A24,24,0,1,0,12,3.2154,24,24,0,0,0,3.2154,36ZM34.3923,18A12,12,0,1,1,18,13.6077,12,12,0,0,1,34.3923,18Z" style={{fill:"none"}} />
            <path d="M24,12H44.7812a23.9939,23.9939,0,0,0-41.5639.0029L13.6079,30l.0093-.0024A11.9852,11.9852,0,0,1,24,12Z" style={{fill:"url(#a)"}} />
            <circle cx="24" cy="24" r="9.5" style={{fill:"#1a73e8"}} />
            <path d="M34.3913,30.0029,24.0007,48A23.994,23.994,0,0,0,44.78,12.0031H23.9989l-.0025.0093A11.985,11.985,0,0,1,34.3913,30.0029Z" style={{fill:"url(#b)"}} />
            <path d="M13.6086,30.0031,3.218,12.006A23.994,23.994,0,0,0,24.0025,48L34.3931,30.0029l-.0067-.0068a11.9852,11.9852,0,0,1-20.7778.007Z" style={{fill:"url(#c)"}} />
          </svg>
          <span className="relative z-10">Get Chrome Extension</span>
        </button>
      </div>
      <p className={`text-sm text-text-secondary-light dark:text-text-secondary-dark mt-6 ${revealText ? 'opacity-100 -translate-y-0 delay-300' : 'opacity-0 translate-y-8 blur-sm'}`}>Get 20 minutes of practice for free. <span className="text-text-primary-light dark:text-text-primary-dark font-semibold">NO CREDIT CARD REQUIRED.</span></p>
    </div>
  );
}